/**
 * Typed view over mapKinds.json — the one map vocabulary.
 *
 * Everything that used to be a hand-kept table (KIND_DISPLAY, CHIP_KINDS, CHIP_WORDS,
 * the 3D CATS palette, the pipeline's KIND_SYNONYMS) derives from that file now, so a
 * chip, its tile, its 3D disc and the legend word that produced it agree by construction.
 * This module only reads; the JSON is the thing to edit.
 */
import vocab from './mapKinds.json';

export type KindFamily =
  'anchor' | 'body' | 'arrival' | 'access' | 'wayfinding' | 'incidental' | 'zone' | 'index';

export type KindRow = {
  label: string;
  /** Short caps word under the chip; null = this kind is never offered as a chip. */
  chip: string | null;
  color: string;
  /** Key into MAP_ICONS (mapIcons.js). */
  glyph: string;
  family: KindFamily;
  /** 3D scene category (priiScene.js CATS / standardize.py). */
  cat: string;
  /** Renders as a 26px glyph tile (vs stages/zones/anchors with their own treatments). */
  service: boolean;
  /** Lowercase legend labels that mean this kind. */
  aliases: string[];
  /** Starts hidden on the 3D map until its chip is on (emergency exits). */
  hidden?: boolean;
};

export type FeatureKind = keyof typeof vocab.kinds;

export const KINDS = vocab.kinds as Record<FeatureKind, KindRow>;

/** Every kind, in vocabulary order (which is chip-row order). */
export const KIND_ORDER = Object.keys(vocab.kinds) as FeatureKind[];

/** Kinds an operator can add in Studio. Anchors and zones first; then the service kinds. */
export const ADDABLE_KINDS = KIND_ORDER.filter((k) => k !== 'other' && k !== 'ga_area');

export function isFeatureKind(value: unknown): value is FeatureKind {
  return typeof value === 'string' && value in KINDS;
}

export function kindsInFamily(family: KindFamily): FeatureKind[] {
  return KIND_ORDER.filter((k) => KINDS[k].family === family);
}

/** Kinds offered as chips, in row order. */
export const CHIP_KINDS: readonly FeatureKind[] = KIND_ORDER.filter((k) => KINDS[k].chip !== null);

/**
 * Chip head kind -> every kind that chip toggles. One chip for ADA: viewing and check-in are the same errand to the
 * person looking (operator, 2026-10-16). Rides/Shuttle/Parking stay split (2026-09-17).
 */
export const CHIP_GROUPS: Readonly<Record<string, readonly FeatureKind[]>> = {
  ada: ['ada', 'ada_checkin'],
};

/** Short caps word under each chip. */
export const CHIP_WORDS: Record<string, string> = Object.fromEntries(
  CHIP_KINDS.map((k) => [k, KINDS[k].chip as string]),
);

const KEYWORDS = Object.entries(vocab.keywords).filter(([k]) => k !== '$comment') as Array<
  [FeatureKind, string[]]
>;

const ALIAS_INDEX: Map<string, FeatureKind> = (() => {
  const m = new Map<string, FeatureKind>();
  for (const k of KIND_ORDER) for (const a of KINDS[k].aliases) m.set(normalizeLabel(a), k);
  return m;
})();

export function normalizeLabel(label: string): string {
  return label
    .toLowerCase()
    .replace(/[–—]/g, '-')
    .replace(/&amp;/g, '&')
    .replace(/[^\p{L}\p{N}&/+-]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Legend label -> kind. Exact alias first, then the ordered keyword rules, then 'other'.
 * Mirrors scripts/festival_map/kinds.py, which reads the same JSON.
 */
export function kindForLabel(label: string): FeatureKind {
  const n = normalizeLabel(label);
  const exact = ALIAS_INDEX.get(n);
  if (exact) return exact;
  for (const [kind, words] of KEYWORDS) {
    if (words.some((w) => n.includes(w))) return kind;
  }
  return 'other';
}

/** The key a split building's slice i keeps its own title under in `kindNames`: its kind for the first of that kind ("food"),
 *  "food:2" and "food:3" for repeats (Sam, 2026-11-01: two food stands under one roof) - so titles saved before repeats
 *  existed read the same. */
export function slotKey(ks: readonly string[], i: number): string {
  const k = ks[i];
  const n = ks.slice(0, i + 1).filter((x) => x === k).length;
  return n > 1 ? `${k}:${n}` : k;
}
